import React, { useState } from 'react';

import {
    App,
    Breadcrumb,
    Button,
    Card,
    Col,
    Collapse,
    Form,
    Input,
    List,
    Modal,
    Popconfirm,
    Row,
    Space,
    Tag,
    Typography,
    UploadFile
} from 'antd';

import { CheckCircleOutlined, CheckCircleTwoTone, ClockCircleOutlined, CloseCircleOutlined, CloseOutlined, DeleteTwoTone, EditTwoTone, ExclamationCircleOutlined, RiseOutlined } from '@ant-design/icons';
import { FilesGalery } from 'components/_Common/FilesGalery';
import Authentication from 'lib/Authentication';
import { formatBRL } from 'lib/helpers/BRL';
import { useServiceBudget } from 'lib/providers/ServiceBudgetContextProvider';
import { Show } from 'lib/Show';
import WhatsAppNumberField from 'lib/WhatsAppNumberField';
import { SendRequestServiceBudget } from 'services/contracts/ServiceBudget.contract';
import { approvedServiceBudgetOption, deleteRequestServiceBudget, sendRequestServiceBudget } from 'services/ServiceBudget.service';
import { updateSupplier } from 'services/Supplier.service';

import { EditRequestServiceBudgetModal } from './EditRequestServiceBudgetModal';
import HistoryModal from './HistoryModal';

export type Values = {
    title: ServiceBudget.Model['title'],
    description: ServiceBudget.Model['description'],
    issue_ids: Array<number>,
    maintenance_ids: Array<number>,
    files?: Array<UploadFile>,
};

export function DetailServiceBudgetModal() {
    const {
        serviceBudget,
        setIsDetailModalVisible,
        setIsRequestModalVisible,
        setIsEditServiceBudgetOptionVisible,
        isEditServiceBudgetOptionVisible,
        setServiceBudgetOption,
        fetchServiceBudgets,
        setServiceBudgetId,
        setIsChangeStatusModalVisible,
        isHistoryModalVisible,
        setIsHistoryModalVisible
    } = useServiceBudget();

    const [isSending, setIsSending] = useState(false);
    const [result, setResult] = useState<null | SendRequestServiceBudget.ResultRequestServiceBudget>(null);
    const [isUpdatingSupplier, setIsUpdatingSupplier] = useState(false);

    const app = App.useApp();

    if (!serviceBudget)
        return null;

    const statusTag = () => {
        const tag = { color: 'warning', value: 'Novo', icon: <ExclamationCircleOutlined /> };

        switch (serviceBudget.status) {
        case 'canceled':
            tag.color = 'error';
            tag.icon = <CloseOutlined />;
            tag.value = 'Cancelado';
            break;
        case 'done':
            tag.color = 'success';
            tag.icon = <CheckCircleOutlined />;
            tag.value = 'Finalizado';
            break;
        case 'pending':
            tag.color = 'gold';
            tag.icon = <ClockCircleOutlined />;
            tag.value = 'Pendente';
            break;
        case 'in_progress':
            tag.color = 'blue';
            tag.icon = <RiseOutlined />;
            tag.value = 'Em progresso';
            break;
        case 'awaiting_approval':
            tag.color = 'processing';
            tag.icon = <ClockCircleOutlined />;
            tag.value = 'Aguardando aprovação';
            break;
        case 'awaiting_for_contract':
            tag.color = 'orange';
            tag.icon = <ClockCircleOutlined />;
            tag.value = 'Aguardando contrato';
            break;
        case 'awaiting_signature':
            tag.color = 'purple';
            tag.icon = <ClockCircleOutlined />;
            tag.value = 'Aguardando assinatura';
            break;
        }

        return <Tag icon={tag.icon} color={tag.color}>{tag.value}</Tag>;
    };


    const usersLinkeds = [
        {
            key: 'users',
            label: 'Usuários vinculados',
            children: (
                <ul>
                    {serviceBudget.users.map(user => (
                        <li key={user.id}>{user.name} #{user.id}</li>
                    ))}
                </ul>
            ),
        }
    ];

    const handleEditServiceBudgetOption = (requestId: number) => {
        const serviceBudgetOption = serviceBudget.requests.find(request => request.id === requestId);

        if (!serviceBudgetOption) {
            throw new TypeError('Request not found in current quote!');
        }

        setIsEditServiceBudgetOptionVisible(true);
        setServiceBudgetOption(serviceBudgetOption);
    };

    const handleDeleteServiceBudgetOption = async (requestId: number) => {
        const serviceBudgetOption = serviceBudget.requests.find(request => request.id === requestId);

        if (!serviceBudgetOption) {
            throw new TypeError('Request not found in current quote!');
        }

        const response = await deleteRequestServiceBudget(serviceBudgetOption.id);
        await fetchServiceBudgets();

        if (!response.success)
            return app.notification.error({ message: 'Atenção', description: response.message });

        app.notification.success({ message: response.message, description: response.description });
    };

    const formattedPrice = new Intl.NumberFormat('pt-BR', {
        style: 'currency',
        currency: 'BRL',
    });

    const handleSendRequestServiceBudget = async (id: number) => {
        setIsSending(true);
        const response = await sendRequestServiceBudget(id);
        setIsSending(false);

        if (!response.success)
            return app.notification.error({ message: 'Atenção', description: response.message });

        setResult(response.result);
    };

    const handleApproveServiceBudget = async (requestId: number) => {
        const serviceBudgetOption = serviceBudget.requests.find(request => request.id === requestId);

        if (!serviceBudgetOption) {
            throw new TypeError('Request not found in current quote!');
        }

        const response = await approvedServiceBudgetOption(serviceBudgetOption.id);
        app.notification.success({ message: response.message, description: response.description });

        fetchServiceBudgets();
        setIsChangeStatusModalVisible(true);
    };

    const handleUpdateSupplier = async ({ email, phone_1, supplier_id }: { email: string, phone_1: string, supplier_id: number }) => {
        setIsUpdatingSupplier(true);
        const response = await updateSupplier(supplier_id, { email, phone_1 });
        setIsUpdatingSupplier(false);
        app.notification.success({ message: response.message, description: response.description });
    };

    const renderFailedItem = (item: SendRequestServiceBudget.ResultItem) => (
        <Typography.Paragraph>
            <CloseCircleOutlined style={{ color: 'red' }} />
            <Typography.Text strong> {item.supplier_name}:</Typography.Text>
            <Typography.Text> {item.message}</Typography.Text>
        </Typography.Paragraph>
    );

    const renderSuccessItem = (item: SendRequestServiceBudget.ResultItem) => (
        <Typography.Paragraph>
            <CheckCircleOutlined style={{ color: 'green' }} />
            <Typography.Text strong> {item.supplier_name}:</Typography.Text>
            <Typography.Text> {item.message}</Typography.Text>
        </Typography.Paragraph>
    );

    const renderResultSendRequest = () => {
        return (
            <Card style={{ marginTop: 10 }} actions={[<Button icon={<CloseOutlined />} onClick={() => setResult(null)}>Fechar</Button>]}>
                <Show when={result?.success !== undefined && result?.success.length > 0}>
                    <List dataSource={result?.success} renderItem={renderSuccessItem} />
                </Show>

                <Show when={result?.error !== undefined && result?.error.length > 0}>
                    <List dataSource={result?.error} renderItem={renderFailedItem} />
                </Show>
            </Card>
        );
    };

    const requests = serviceBudget.requests.map((request, key) => {
        return {
            key: `${request.id}${key}`,
            label: (
                <span>
                    {request.supplier.name} ({formatBRL(request.price)})
                    {request.id === serviceBudget.request_approved?.id && <Tag color="success" icon={<CheckCircleOutlined />} style={{ marginLeft: 8 }}>Aprovado</Tag>}
                </span>
            ),
            children: (
                <>
                    <Form
                        key={request.id}
                        layout="vertical"
                        initialValues={{ email: request.supplier.email, phone_1: request.supplier.phone_1, supplier_id: request.supplier.id }}
                        onFinish={handleUpdateSupplier}
                        disabled={isUpdatingSupplier}>
                        <Form.Item label="Email" name="email" style={{ marginBottom: 0 }}>
                            <Input type='email' />
                        </Form.Item>
                        <WhatsAppNumberField name='phone_1' />
                        <Form.Item hidden name="supplier_id">
                            <Input type='number' />
                        </Form.Item>
                        <Button loading={isUpdatingSupplier} size='small' type="primary" htmlType="submit" style={{ marginTop: 10 }}>
                        Atualizar fornecedor
                        </Button>
                    </Form>
                    <p>Preço: {formattedPrice.format(request.price)}</p>
                    <FilesGalery files={request.files} />
                </>
            ),
            extra: (
                <Space size="middle">
                    <Show when={request.id !== serviceBudget.request_approved?.id && serviceBudget.status === 'awaiting_approval'}>
                        <Popconfirm
                            title="Aprovar orçamento"
                            description="Você tem certeza que deseja aprovar o orçamento com esta solicitação?"
                            onConfirm={() => handleApproveServiceBudget(request.id)}
                            okText="Sim"
                            cancelText="Não"
                        >
                            <Button size='small' type='primary'>{serviceBudget.request_approved !== null ? 'Mudar aprovação' : 'Aprovar'} <CheckCircleTwoTone /></Button>
                        </Popconfirm>
                    </Show>

                    <Show when={request.price === 0}>
                        <Tag color="orange">Pendente</Tag>
                    </Show>

                    <Show when={serviceBudget.status !== 'done' && serviceBudget.status !== 'canceled'}>
                        <EditTwoTone
                            onClick={() => handleEditServiceBudgetOption(request.id)}
                            title='Editar'
                            style={{ marginRight: 8 }}
                        />

                        <Popconfirm
                            title="Excluir solicitação"
                            description="Você tem certeza que deseja excluir esta solicitação?"
                            onConfirm={() => handleDeleteServiceBudgetOption(request.id)}
                            okText="Sim"
                            cancelText="Não"
                        >
                            <DeleteTwoTone
                                title='Excluir'
                                style={{ marginRight: 8 }}
                            />
                        </Popconfirm>
                    </Show>
                </Space>
            ),
        };
    });

    const items = serviceBudget.issues.map((i, key) => ({
        key: `${i.id}${key}`,
        label: `${i.issue_type.name} #${i.id} - ${i.subject}`,
        children: <>
            {i.type === 'text' && <p>{i.description}</p>}
            {i.type === 'checklist' && (i.issue_checklist.map(checklist => (
                <>
                    <p>{checklist.name}</p>
                    <ul>
                        {checklist.tasks.map(task => (
                            <li key={task.id}>
                                {task.type === 'approved' && <CheckCircleOutlined />}
                                {task.type === 'disapproved' && <ClockCircleOutlined />}
                                {task.description}
                            </li>
                        ))}
                    </ul>
                </>
            )))}
            <FilesGalery files={i.issue_file} />
        </>
    }));

    items.push(...serviceBudget.maintenances.map((m, key) => ({
        key: `${m.id}${key}`,
        label: `Manutenção #${m.id} - ${m.maintenance_type ? m.maintenance_type.name : m.description}`,
        children: <div dangerouslySetInnerHTML={{ __html: m.description }}></div>
    })));

    items.push(...serviceBudget.constructions.map((m, key) => ({
        key: `${m.id}${key}`,
        label: `Obra #${m.id} ${m.name}`,
        children: <>
            {m.description ?? 'Sem descrição'}
        </>
    })));

    const handleChangeStatus = () => {
        setServiceBudgetId(serviceBudget.id);
        setIsChangeStatusModalVisible(true);
    };

    const close = () => {
        setIsDetailModalVisible(false);
        setServiceBudgetId(null);
    };

    const hasWhatsappIntegration = Authentication.hasWhatsappIntegration();
    const hasSupplierEmail = serviceBudget.requests.some(request => request.supplier?.email);
    const isApproved = serviceBudget.request_approved !== null;
    
    const isDisable = !hasWhatsappIntegration && !hasSupplierEmail && isApproved;
    
    let message = '';
    if (hasWhatsappIntegration && !hasSupplierEmail) {
        message = 'Será enviado apenas via WhatsApp.';
    } else if (!hasWhatsappIntegration && hasSupplierEmail) {
        message = 'Será enviado apenas via e-mail.';
    } else if (!hasWhatsappIntegration && !hasSupplierEmail) {
        message = 'Nenhum contato disponível (e-mail ou WhatsApp).';
    }
    const title = (
        <Row justify="space-between" style={{ marginRight: 20 }}>
            <Breadcrumb
                style={{
                    fontSize: 18,
                    color: '#000'
                }}
                separator="|"
                items={[
                    {
                        title: serviceBudget.client.name,
                    },
                    {
                        title: `Orçamento #${serviceBudget.id}`,
                    },
                    {
                        title: serviceBudget.title,
                    },
                    {
                        title: statusTag()
                    },
                ]}
            />
        </Row>
    );

    return (
        <Modal
            open
            width={'70%'}
            onCancel={close}
            footer={false}
            title={title}
            style={{ top: 10, height: 'auto' }}
        >
            <Row>
                <Col xs={24} sm={24} md={12} lg={12}>
                    <Card bordered={false}>

                        <Typography.Paragraph>
                            <strong>Descrição: </strong>
                            <div style={{ whiteSpace: 'pre-line' }} dangerouslySetInnerHTML={{ __html: serviceBudget.description }}></div>
                        </Typography.Paragraph>

                        <Show when={serviceBudget.who !== null}>
                            <Typography.Paragraph>
                                <strong>Responsável: </strong>
                                {serviceBudget.who?.name}
                            </Typography.Paragraph>
                        </Show>

                        <Show when={items.length > 0}>
                            <Typography.Paragraph style={{ margin: 0, marginBottom: 10 }}><strong>Referências vinculadas:</strong></Typography.Paragraph>
                            <Collapse items={items} style={{ maxHeight: 'auto', overflowY: 'auto', marginBottom: 10 }} />
                        </Show>

                        <Show when={serviceBudget.users.length > 0}>
                            <Typography.Paragraph style={{ margin: 0, marginBottom: 10, marginTop: 10 }}><strong>Usuários vinculados:</strong></Typography.Paragraph>
                            <Collapse
                                items={usersLinkeds}
                                style={{ maxHeight: 'auto', overflowY: 'auto', marginBottom: 10 }}
                            />
                        </Show>

                        <FilesGalery files={serviceBudget.files} />
                    </Card>
                </Col>
                <Col xs={24} sm={24} md={12} lg={12}>
                    <Row
                        justify={serviceBudget.status !== 'done' && serviceBudget.status !== 'canceled' ? 'space-between' : 'end'}
                        align="middle"
                        gutter={[10, 10]}
                        style={{ marginBottom: 16 }}
                    >
                        <Show when={serviceBudget.status !== 'done' && serviceBudget.status !== 'canceled'}>
                            <Col>
                                <Button onClick={() => handleChangeStatus()}>
                                    Atualizar status
                                </Button>
                            </Col>
                        </Show>

                        <Show when={serviceBudget.status !== 'done' && serviceBudget.status !== 'canceled'}>
                            <Col>
                                <Button type="primary" onClick={() => setIsRequestModalVisible(true)}>
                                    Cadastrar solicitação
                                </Button>
                            </Col>
                        </Show>

                        <Col>
                            <Button type='dashed' onClick={() => setIsHistoryModalVisible(true)}>
                                Histórico
                            </Button>
                        </Col>
                    </Row>

                    <Show when={requests.length > 0}>
                        <Typography.Paragraph style={{ marginBottom: 10 }}>
                            <strong>Solicitações:</strong>
                        </Typography.Paragraph>

                        <Show when={serviceBudget.request_approved === null && serviceBudget.status === 'awaiting_approval'}>
                            <Typography.Paragraph>
                                Para aprovar um orçamento, clique no botão aprovar no orçamento desejado.
                            </Typography.Paragraph>
                        </Show>

                        <Collapse
                            items={requests}
                            style={{ maxHeight: '30rem', overflowY: 'auto', borderRadius: 8 }}
                        />

                        <Show when={!isApproved}>
                            <Popconfirm
                                title="Solicitar orçamentos"
                                description={message || 'Você tem certeza que deseja solicitar orçamentos para os fornecedores?'}
                                onConfirm={() => handleSendRequestServiceBudget(serviceBudget.id)}
                                okText="Sim, tenho certeza"
                                cancelText="Não"
                                disabled={isDisable}
                            >
                                <Button
                                    type="primary"
                                    loading={isSending}
                                    style={{ marginTop: 10 }}
                                    disabled={isDisable}
                                >
                                    Solicitar orçamentos
                                </Button>
                            </Popconfirm>

                            <Show when={result !== null}>
                                {renderResultSendRequest()}
                            </Show>
                        </Show>
                    </Show>


                    <Show when={isEditServiceBudgetOptionVisible}>
                        <EditRequestServiceBudgetModal />
                    </Show>
                    <Show when={isHistoryModalVisible}>
                        <HistoryModal />
                    </Show>
                </Col>
            </Row>
        </Modal>
    );
}
